import { ButtonType } from '@tableau/widgets';
import * as React from 'react';
import { defaultMemoize } from 'reselect/lib';
import * as _ from 'underscore';

import { Formatters, Messages } from '../../codegen/Localize';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { MultilineTextField } from '../../common/react/components/MultilineTextField';
import { SiteRolePickerMenu } from '../../common/react/components/SiteRolePickerMenu';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { SiteRolePickerMenuHelper } from '../../ts/helpers/SiteRolePickerMenuHelper';
import { UsernameHelpers } from '../../ts/helpers/UsernameHelpers';
import { Set } from '../../ts/models/Set';
import { IImportUsersFromActiveDirectoryResult } from '../../ts/ServerApi/requests/ImportUsersFromActiveDirectoryRequest';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { UserActionNotification } from '../services/notifications/UserActionNotification';
import {
  DefaultSiteRole,
  SiteRoleOption,
  SiteRoleOptionsResource
} from '../services/resources/SiteRoleOptions';
import { SiteUsers } from '../services/resources/SiteUsers';
import { ResultCode } from '../services/UserActionResultCode';
import { SiteRoleInfoLink } from './SiteRoleInfoLink';

export interface AddActiveDirectorySiteUsersDialogState {
  readonly siteRole: string;
  readonly busy: boolean;
  readonly userInput: string;
}

export interface AddActiveDirectorySiteUsersDialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
  readonly availableQuota: number;
}

const getDisabledRoleOptionsHandler = defaultMemoize((availableQuota: number, siteRoleOptions: SiteRoleOption[]): ReadonlyArray<SiteRoleOption> => SiteRolePickerMenuHelper.getPickerMenuDropdownOptionDisabledOptions(availableQuota, siteRoleOptions));

export class AddActiveDirectorySiteUsersDialog extends React.Component<AddActiveDirectorySiteUsersDialogProps, AddActiveDirectorySiteUsersDialogState> {
  private siteRolesOptionResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;
  private server: SessionManagementService = getSessionManagementServiceInstance();
  private userActionNotification: UserActionNotification = UserActionNotification.instance;

  public constructor(props: AddActiveDirectorySiteUsersDialogProps) {
    super(props);
    this.state = {
      siteRole:
        this.props.availableQuota <= 0 ? SiteRoles.Unlicensed : DefaultSiteRole,
      busy: false,
      userInput: ''
    };
  }

  private readonly importUsersCallback = () => {
    this.importUsers();
  }

  private readonly handleChangeCallback = (userInput: string) => {
    this.setState({userInput});
  }

  private readonly getSiteRoleOptionBySiteRoleCallback = (siteRole: string): SiteRoleOption => {
    return this.siteRolesOptionResource.getSiteRoleOptionBySiteRole(siteRole);
  }

  private readonly onOptionSelectedCallback = (siteRoleOption: SiteRoleOption): void => {
    this.onSiteRoleSelected(siteRoleOption);
  }

  private siteRoleOptions = this.server.isSessionServerAdmin() === true ? this.siteRolesOptionResource.getAllSiteRoleOptions() : this.siteRolesOptionResource.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);

  public render() {
    return (
      <div className="tb-add-ad-users-dialog">
        <div className="tb-dialog-title">{Messages.addAdUsersTitle()}</div>
        <div>{Messages.addAdUsersDesc()}</div>
        <div className="tb-dialog-body-control tb-multiline-text-holder">
          <MultilineTextField
            handleChange={this.handleChangeCallback}
            disabled={this.state.busy}
            testId="add-ad-users-input"
          />
        </div>
        {this.availableQuotaMessage()}
        <div className="tb-row">
          <span className="tb-row-height-box tb-margin-right">
            {Messages.siteRole()}
          </span>
          <span className="tb-row-with-icon-on-right">
            <SiteRolePickerMenu
              siteRoleOptions={this.siteRoleOptions}
              getSiteRoleOptionBySiteRole={this.getSiteRoleOptionBySiteRoleCallback}
              onOptionSelected={this.onOptionSelectedCallback}
              selectedRole={this.siteRolesOptionResource.getSiteRoleOptionBySiteRole(this.state.siteRole)}
              isDisabled={this.state.busy}
              disabledRoleOptions={[...getDisabledRoleOptionsHandler(this.props.availableQuota, this.siteRoleOptions)]}
            />
          </span>
          <SiteRoleInfoLink/>
        </div>
        {this.noAvailableQuotaMessage()}
        <div className="tb-dialog-actions">
          <ButtonWithSpinner
            buttonType={ButtonType.Outline}
            showSpinner={false}
            clicked={this.props.dismiss}
            testId={'confirm-action-dialog-cancel'}
            title={Messages.cancel()}
            disabled={this.state.busy}
          />
          <span className="tb-lr-padded">
            <ButtonWithSpinner
              buttonType={ButtonType.Go}
              showSpinner={this.state.busy}
              clicked={this.importUsersCallback}
              testId={'confirm-action-dialog-submit'}
              title={Messages.importUsers_Submit()}
              disabled={this.state.busy || this.state.userInput.length === 0}
            />
          </span>
        </div>
      </div>
    );
  }

  private availableQuotaMessage() {
    if (_.isNumber(this.props.availableQuota)) {
      return (
        <div className={'tb-available-quota tb-row'}>
        {Messages.availableQuota({NUM_USERS: this.props.availableQuota, NUM_USERS_LOC: Formatters.formatCount(this.props.availableQuota)})}
        </div>
      );
    }
    return null;
  }

  private noAvailableQuotaMessage() {
    if (this.props.availableQuota <= 0) {
      return (
        <div className="tb-no-quota-available-message">
          {Messages.addUsers_noLicensesAvailable()}
        </div>
      );
    }
    return null;
  }

  private onSiteRoleSelected(siteRoleOption: SiteRoleOption): void {
    this.setState({siteRole: siteRoleOption.role});
  }

  private importUsers(): Promise<any> {
    this.setState({busy: true});
    let usersToAdd = UsernameHelpers.splitUsernames(this.state.userInput); // match strings delimited by semicolons & commas
    let siteRole = this.state.siteRole;

    let serverAdmin = false;
    if (this.state.siteRole === SiteRoles.ServerAdministrator) {
      siteRole = SiteRoles.Unlicensed;
      serverAdmin = true;
    }
    return SiteUsers.instance
      .importUsersFromActiveDirectory(usersToAdd, siteRole, serverAdmin)
      .then((result: IImportUsersFromActiveDirectoryResult) => {
        // DO NOT SET STATE in this then clause
        this.userActionNotification.notify({
          users: Set.fromIdItemArray(result.users),
          results: result.results,
          errors: result.errors,
          successCode: ResultCode.SiteUserCreated.toString(),
          siteRole: siteRole
        });
        return  Promise.resolve({});
      })
      .then((result: any) => this.props.close(result));
  }
}
